import { useCallback, useState } from "react";
import {
	convertLiveSessionsToPendingSessions,
	EditQuizAssessmentUUids,
	getLiveSessionErrors,
	getLiveSessionsCancelledByUpdate,
	LiveSessionWithInvitations,
	PendingLiveSession,
	ProctorGroupInfo,
	ProctorInfo,
	resetStartedLiveSessions,
	stripSeconds,
} from "../../live/liveHelper";
import { coreToast } from "../../../core";
import { UseEditQuizStateProps } from "./useEditQuizState";
import i18nPackage from "../../../i18n/i18n";

type PendingLiveSessionUpdate = Pick<
	PendingLiveSession,
	"name" | "startTime" | "endTime" | "capacity" | "registrationStartTime" | "registrationEndTime"
>;

type UseLiveStateProps = Pick<
	UseEditQuizStateProps,
	| "api"
	| "quizInfo"
	| "existingLiveSessions"
	| "setExistingLiveSessions"
	| "pendingLiveSessions"
	| "setPendingLiveSessions"
	| "pendingLiveSessionIds"
	| "setPendingLiveSessionIds"
	| "currFakeLiveSessionId"
>;

/**Anything to do with live state */
export default function useLiveState({
	api,
	quizInfo,
	existingLiveSessions,
	setExistingLiveSessions,
	pendingLiveSessions,
	setPendingLiveSessions,
	pendingLiveSessionIds,
	setPendingLiveSessionIds,
	currFakeLiveSessionId,
}: UseLiveStateProps) {
	const { getCourseInstructors, getInstituteProctorGroups, getAssessmentLiveSessions, getLiveSessionInvitations } = api;

	const [availableProctors, setAvailableProctors] = useState<Record<string, ProctorInfo>>({});
	const [availableProctorGroups, setAvailableProctorGroups] = useState<Record<number, ProctorGroupInfo>>({});
	const [liveSessionsNextConfirmationMessage, setLiveSessionsNextConfirmationMessage] = useState<string | undefined>();

	const getNextFakeLiveSessionId = () => `#${currFakeLiveSessionId.current++}`;

	const validateLiveSession = useCallback(() => {
		const liveSessionErrors = getLiveSessionErrors(existingLiveSessions, pendingLiveSessions, availableProctorGroups);
		const cancelledLiveSessions = getLiveSessionsCancelledByUpdate(
			existingLiveSessions,
			pendingLiveSessions,
			availableProctorGroups,
		);
		if (Object.values(liveSessionErrors).some((errors) => errors.length > 0)) {
			const sessionNamesList = Object.entries(liveSessionErrors)
				.filter(([, errors]) => errors.length > 0)
				.map(([id]) => pendingLiveSessions[id]?.name || "[Unnamed]");
			let sessionNames: string;
			if (sessionNamesList.length === 1) {
				[sessionNames] = sessionNamesList;
			} else {
				sessionNames = `${sessionNamesList.slice(0, -1).join(", ")} and ${sessionNamesList[sessionNamesList.length - 1]}`;
			}
			coreToast.error(
				Object.values(liveSessionErrors).some((errors) => errors.includes("noSessions"))
					? i18nPackage.t("editQuizPage.liveSessionStep.error.noLiveSessionMessage")
					: i18nPackage.t("editQuizPage.liveSessionStep.error.sessionIssueMessage", { sessionNames }),
				{ containerId: `toast-${quizInfo.quizId}` },
			);
			return false;
		}
		if (cancelledLiveSessions.length > 0) {
			setLiveSessionsNextConfirmationMessage(
				i18nPackage.t("editQuizPage.liveSessionStep.error.sessionWarningMessage", {
					cancelledSessionNames: cancelledLiveSessions.map(({ name }) => name).join(", "),
				}),
			);
		}
		return true;
	}, [existingLiveSessions, pendingLiveSessions, availableProctorGroups]);

	const fetchAndSetExistingLiveSessions = useCallback(
		async (assessmentUuids: EditQuizAssessmentUUids | undefined): Promise<LiveSessionWithInvitations[]> => {
			if (!assessmentUuids) {
				throw new Error("no assessmentUuids were obtained");
			}
			if (!quizInfo.courseId) {
				throw new Error("no course ID");
			}

			const instructors = await getCourseInstructors(quizInfo.courseId);
			setAvailableProctors(Object.fromEntries(instructors.map(({ uuid, name }) => [uuid, { id: uuid, name }])));
			setAvailableProctorGroups(
				Object.fromEntries(
					(await getInstituteProctorGroups(assessmentUuids.instituteUuid)).map((proctorGroup) => [
						proctorGroup.id,
						{ ...proctorGroup, autoCancelEmptyHoursBefore: proctorGroup.autoCancelEmptyHoursBefore ?? undefined },
					]),
				),
			);

			const sessions = assessmentUuids.assessmentUuid
				? (
						await getAssessmentLiveSessions({
							...assessmentUuids,
							assessmentUuid: assessmentUuids.assessmentUuid,
						})
					).results
				: [];
			const invitations = Object.fromEntries(
				(
					await Promise.allSettled(
						sessions.map(async ({ id }) => [
							id,
							(await getLiveSessionInvitations(id)).filter(({ state }) => !state.cancelledAt),
						]),
					)
				)
					.filter((result) => result.status === "fulfilled")
					.map(
						(result) =>
							(
								result as PromiseFulfilledResult<
									[
										string,
										{
											liveSessionId: string;
											proctorGroupId: number;
										}[],
									]
								>
							).value,
					),
			);
			const sessionsWithInvitations = sessions.map((session) => ({
				...session,
				invitations: invitations[session.id] ?? [],
			}));
			setExistingLiveSessions(Object.fromEntries(sessionsWithInvitations.map((session) => [session.id, session])));
			return sessionsWithInvitations;
		},
		[
			getAssessmentLiveSessions,
			getCourseInstructors,
			getInstituteProctorGroups,
			getLiveSessionInvitations,
			quizInfo.courseId,
			setAvailableProctorGroups,
			setAvailableProctors,
			setExistingLiveSessions,
		],
	);

	const fetchAndSetLiveSessions = useCallback(
		async (assessmentUuids: Parameters<typeof fetchAndSetExistingLiveSessions>[0]) => {
			// let this still run to populate available proctors
			const sessions = await fetchAndSetExistingLiveSessions(assessmentUuids);
			if (!quizInfo.quizId) return;

			setPendingLiveSessionIds(sessions.map(({ id }) => id));
			setPendingLiveSessions(convertLiveSessionsToPendingSessions(sessions));
		},
		[fetchAndSetExistingLiveSessions, setPendingLiveSessionIds, setPendingLiveSessions],
	);

	const getNextSessionNumber = useCallback(() => {
		const regex = /^Session (\d+)\s*(?:\(Demo\))?\s*$/;
		return (
			Math.max.apply(
				null,
				Object.values(pendingLiveSessions)
					.map(({ name }) => {
						const result = regex.exec(name);
						return result ? Number(result[1]) : 0;
					})
					.concat([0]),
			) + 1
		);
	}, [pendingLiveSessions]);

	const addPendingSession = useCallback(() => {
		const identifier = getNextFakeLiveSessionId();
		const currTimestamp = Date.now();
		setPendingLiveSessionIds(pendingLiveSessionIds.concat([identifier]));
		setPendingLiveSessions({
			...pendingLiveSessions,
			[identifier]: {
				identifier,
				name: i18nPackage.t("editQuizPage.liveSessionStep.defaultName", { number: getNextSessionNumber() }),
				startTime: stripSeconds(currTimestamp + 60 * 60 * 1000),
				endTime: stripSeconds(currTimestamp + 2 * 60 * 60 * 1000),
				capacity: 50,
				proctorId: Object.values(availableProctors)[0]?.id,
			},
		});
		return identifier;
	}, [availableProctors, getNextSessionNumber, pendingLiveSessionIds, pendingLiveSessions]);

	const updatePendingSession = useCallback(
		(id: string, update: PendingLiveSessionUpdate) => {
			setPendingLiveSessions(
				Object.fromEntries(
					Object.values(pendingLiveSessions).map((session) => [
						session.identifier,
						session.identifier === id ? { ...session, ...update } : session,
					]),
				),
			);
		},
		[pendingLiveSessions],
	);

	const removePendingSession = useCallback(
		(id: string) => {
			setPendingLiveSessionIds(pendingLiveSessionIds.filter((sessionId) => sessionId !== id));
			setPendingLiveSessions(
				Object.fromEntries(Object.entries(pendingLiveSessions).filter(([sessionId]) => sessionId !== id)),
			);
		},
		[pendingLiveSessionIds, pendingLiveSessions],
	);

	const syncStartedSessions = useCallback(() => {
		setPendingLiveSessions((prev) => {
			const next = resetStartedLiveSessions(existingLiveSessions, prev);
			// Only update state if something actually changed to avoid infinite loops
			return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
		});
	}, [existingLiveSessions]);

	const resetConfirmationMessage = useCallback(() => {
		setLiveSessionsNextConfirmationMessage(undefined);
	}, []);

	return {
		state: {
			pendingLiveSessionIds,
			existingLiveSessions,
			pendingLiveSessions,
			availableProctors,
			availableProctorGroups,
			liveSessionsNextConfirmationMessage,
		},
		action: {
			validateLiveSession,
			syncStartedSessions,
			fetchAndSetLiveSessions,
			fetchAndSetExistingLiveSessions,
			resetConfirmationMessage,
			updatePendingSession,
			removePendingSession,
			addPendingSession,
		},
	};
}
